import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import OnboardingStoreService from 'legacy/lut/classes/services/OnboardingStoreService'
import { useTriggerRefreshRequestDetails } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/hooks/useTriggerRefreshRequestDetails'
import { useCallback, useEffect, useReducer, useState } from 'react'

import { fail } from 'onboarding/classes/services/notification'
import { requestService } from 'onboarding/services/requestService'
import { showErrorOnCatch } from 'onboarding/utils/errorResponse/showErrorOnCatch'

import { EActionType, EActiveContent, initialState, reducer } from '../reducer'

interface IProps {
  closeModal: () => void
  requestId: number
  requestType: RequestTypes
}

export enum EPendingRequestStatus {
  none = 'none',
  exist = 'exist',
  absent = 'absent',
}

export const useModalStateAndHandlers = ({ closeModal, requestId, requestType }: IProps) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [pendingRequestStatus, setIsPendingRequestOfChosenPartner] = useState(
    EPendingRequestStatus.none
  )
  const { triggerRefreshRequestDetails } = useTriggerRefreshRequestDetails()
  const resetPendingRequestStatus = useCallback(
    () => setIsPendingRequestOfChosenPartner(EPendingRequestStatus.none),
    [pendingRequestStatus]
  )

  // check for existing pending request for chosen partner
  useEffect(() => {
    const getPendingRequestForPartner = async () => {
      if (state.partnerId) {
        showPreloader()

        const response = await requestService.fetchOnePendingByPartnerAndType(
          state.partnerId,
          requestType
        )

        hidePreloader()

        if (response && response.status === 'success' && response.data) {
          setIsPendingRequestOfChosenPartner(
            response.data.pendingRequest === null
              ? EPendingRequestStatus.absent
              : EPendingRequestStatus.exist
          )
          dispatch({
            type: EActionType.SetExistingPendingRequestOfChosenPartner,
            payload: {
              existingPendingRequestOfChosenPartner: response.data.pendingRequest,
            },
          })
        } else {
          fail(
            response.message ||
              `Failed to getPendingRequestForPartner by partnerId = ${state.partnerId} and requestType = ${requestType}`
          )
        }
      }
    }

    getPendingRequestForPartner()
  }, [state.partnerId, requestType])

  // handlers:
  const cancelAndCloseModal = () => {
    dispatch({ type: EActionType.Reset })
    closeModal()
  }

  const showPreloader = () => dispatch({ type: EActionType.ShowPreloader })
  const hidePreloader = () => dispatch({ type: EActionType.HidePreloader })

  const selectPartnerId = useCallback(
    async (partnerId: string) => {
      if (requestType === RequestTypes.Contract) {
        showPreloader()

        const agreements = await OnboardingStoreService.getPartnerAgreements(partnerId)

        hidePreloader()

        if (agreements.length > 0) {
          dispatch({
            type: EActionType.GoToConfirm,
            payload: {
              agreementsToConfirm: agreements.map((agreement) => ({
                code: agreement.Code || '-',
                codeType: agreement.CodeType || '-',
                id: agreement.AgreementNo || '-',
              })),
              partnerIdToConfirm: partnerId,
            },
          })

          return
        }
      }
      changePartnerId(partnerId)
    },
    [requestType]
  )

  const changePartnerId = (partnerId: string) => {
    dispatch({ type: EActionType.ChangePartnerId, payload: { partnerId } })
  }

  const goToSearchContent = () =>
    dispatch({
      type: EActionType.ChangeActiveContent,
      payload: { activeContent: EActiveContent.Search },
    })

  const goToSelectContent = () =>
    dispatch({
      type: EActionType.ChangeActiveContent,
      payload: { activeContent: EActiveContent.Select },
    })

  const confirmPartnerId = () =>
    dispatch({
      type: EActionType.ConfirmPartnerId,
      payload: {
        partnerId: state.partnerIdToConfirm,
      },
    })

  const submitChangePartner = useCallback(async () => {
    try {
      showPreloader()
      const isChanged = await requestService.changePartner(requestId, state.partnerId)
      if (isChanged) {
        cancelAndCloseModal()
        triggerRefreshRequestDetails()
      }
    } catch (error: any) {
      showErrorOnCatch(error)
    } finally {
      hidePreloader()
    }
  }, [cancelAndCloseModal, requestId, state.partnerId, triggerRefreshRequestDetails])

  return {
    state,
    handlers: {
      cancelAndCloseModal,
      selectPartnerId,
      changePartnerId,
      goToSearchContent,
      goToSelectContent,
      confirmPartnerId,
      submitChangePartner,
      showPreloader,
      hidePreloader,
    },
    pendingRequestStatus,
    resetPendingRequestStatus,
  }
}
